import { ICertificateData } from '@app/certificates/manage/manage';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { IStatusIcon, ITranslation } from '@app/shared/interfaces/common-interfaces';

export type RequestedHistoryRequest = {
  relayingParty?: string;
  status?: string;
  textToShow?: string;
  fromCreatedDate?: string;
  toCreatedDate?: string;
  orderByFilter?: string;
  order?: string;
  offset?: number;
  limit?: number;
  additionalParams?: string[];
  companyCmrId?: string;
  employeeCmrId?: string;
  parcelType?: string;
  isEmployeeCc?: boolean;
  sender?: string;
  recipient?: string;
};

export type RequestedHistoryDetailsRequest = {
  relayingParty?: string;
  requestedId?: number;
  additionalParams?: string[];
};

export type RequestedHistory = {
  id?: number;
  requestedId?: number;
  userId?: number;
  requestedDate?: number;
  requestedDownloadDate?: number;
  lastUsageId?: number;
  signCount?: number;
  reqSignCount?: number;
  lastStatusId?: number;
  rpName?: string;
  rpId?: number;
  fileName?: string;
  archiveStatus?: number;
  fileId?: string;
  status?: string;
  statusIcon?: IStatusIcon;
  name?: string;
  docCount?: number;
  signatureTs: number,
  downloadTs: number,
  ts: number,
  parcelType: string,
  senderCmrId: string,
  serviceEventId: string
};

export type RequestedHistoryDetails = {
  status?: string;
  statusIcon?: IStatusIcon;
  signDate?: string;
  firstName?: ITranslation;
  lastName?: ITranslation;
  dnCn?: string;
  fileId?: string;
  deadLineTs?: string;
};

export type SigningHistory = {
  signedDate?: number;
  date?: number;
  documentName?: string;
  certId?: number;
  certSerialNumber?: number;
  relyingPartyId?: number;
  relyingPartyName?: string;
  status: string;
  statusIcon?: IStatusIcon;
  createdDate?: number;
  filename?: string;
  displayedText?: string;
  fileId?: string;
  parcelUsageId?: number;
  usageIdDownloadRequested?: number;
  objectType?: string,
  documentCount?: number,
  //
  receivedSignedDate?: number,
  contentTypeId?: number,
  id?: number,
  senderBg?: string,
  senderEn?: string,
  contentFormat?: string,
  archiveStatus?: number
};

export type SigningHistoryRequest = {
  relayingParty?: string;
  status?: string;
  certId?: number;
  certificateNumber?: string[];
  signatureType?: string;
  textToShow?: string;
  fromCreatedDate?: string;
  toCreatedDate?: string;
  orderByFilter?: string;
  order?: string;
  offset?: number;
  limit?: number;
  isLogin?: boolean;
  additionalParams?: string[];
  companyCmrId?: string;
  employeeCmrId?: string;
  type?: string;
  sender?: string;
};

export enum AdditionalParams {
  ARCHIVE_STATUS = 'archiveStatus',
  TO_BE_ARCHIVED = 'toBeArchived'
}

export enum ContentType {
  NORMAL_DOCUMENT = 0,
  LOGIN = 10,
  EAUTH_REQUEST = 20,
  AR_CONSENT = 30,
  CQES_REQUEST = 100,
  CQES_CONTRACT = 110,
}

export type OrderHistoryRequest = {
  orderCode?: string;
  status?: number;
  planId?: string;
  fromOrderDate?: string;
  toOrderDate?: string;
  orderByFilter?: string;
  order?: string;
  offset?: number;
  limit?: number;
  price?: number;
  planSigningCount?: number;
  paymentDate?: string;
};

export type PurchaseHistory = {
  id: number;
};

export type HistoryData = {
  selectedCertId: ICertificateData['certId'];
  signing: SigningHistory[];
  orders: IOrder[];
};

export interface IOrder {
  orderId: any;
  orderCode: string;
  orderDate: number;
  paymentDate: number;
  orderStatus: number;
  planId: number;
  planName: object;
  price: any;
  currencyCode: string;
  planSigningCount: number;
  planType: string;
  axOrderNumber: string;
  axInvoiceNumber: string;
}

export type PaymentDataStatus = {
  amount: string,
  orderNumber: string,
  transactionDate: string,
  responseCode: string,
  transactionStatus: string,
  statusCode: string,
  message: string,
  status: string,
  cardHolderInfo: string
}

export type PaymentData = {
  amount: string,
  currency: string,
  description: string,
  terminal: string,
  merchName: string,
  merchant: string,
  trType: string,
  order: string,
  timestamp: string,
  nonce: string,
  lang: string,
  backRef: string,
  addendum: string,
  adCustBorOrderId: string,
  pSign: string
}

export type OrderStatus = {
  key: number,
  text: string,
  icon: string,
  iconClass: string
};

export const OrderStatusEnum = {
  CANCELED: -1,
  IN_PROGRESS: 1,
  PAID: 2,
  NEW: 0,
  ALL: -100
};

export enum DocumentStatus {
  ERROR = -1,
  REQUESTED_SIGN_BY_RELYING_PARTY = 0,
  PIN_APPROVED_BY_OWNER = 1,
  SUCCESSFULLY_SIGNED = 2,
  RECEIVED_FROM_RELYING_PARTY = 3,
  REJECTED_BY_OWNER = 4,
  DEVICE_ADDED = 5,
  DEVICE_REMOVED = 6,
  ARCHIVED = 7,
  DELETED = 8
}

export const OrderStatusMap = new Map<number, OrderStatus>();

OrderStatusMap.set(OrderStatusEnum.CANCELED, {
  key: OrderStatusEnum.CANCELED,
  text: 'ordered',
  icon: iconCodes.blocked,
  iconClass: 'text-danger',
  tooltip: 'historyTexts.orderStatusCanceled'
} as OrderStatus);

OrderStatusMap.set(OrderStatusEnum.IN_PROGRESS, {
  key: OrderStatusEnum.IN_PROGRESS,
  text: 'ordered',
  icon: iconCodes.bookmark_border,
  iconClass: 'text-notice',
  tooltip: 'historyTexts.orderStatusOrdered'
} as OrderStatus);

OrderStatusMap.set(OrderStatusEnum.PAID, {
  key: OrderStatusEnum.PAID,
  text: 'paid',
  icon: iconCodes.done,
  iconClass: 'text-success',
  tooltip: 'historyTexts.orderStatusPaid'
} as OrderStatus);

OrderStatusMap.set(OrderStatusEnum.NEW, {
  key: OrderStatusEnum.NEW,
  text: 'new',
  icon: iconCodes.fiber_new,
  iconClass: 'text-success',
  tooltip: 'historyTexts.orderStatusNew'
} as OrderStatus);

export class HistoryOrder {
  orderId: number;
  orderCode: string;
  orderDate: number;
  paymentDate: number;
  certId: number;
  certSerialNumber: string;
  planId: number;
  planName: object;
  price: number;
  currencyCode: string;
  status: OrderStatus;
  planSigningCount: number;
  planType: string;
  axOrderNumber: string;
  axInvoiceNumber: string;
  transactionNumber: string;

  constructor(order: IOrder) {
    this.orderId = order.orderId;
    this.orderCode = order.orderCode;
    this.orderDate = order.orderDate;
    this.paymentDate = order.paymentDate;
    this.planId = order.planId;
    this.planName = order.planName;
    this.price = order.price;
    this.currencyCode = order.currencyCode;
    this.status = OrderStatusMap.get(order.orderStatus);
    this.planSigningCount = order.planSigningCount;
    this.planType = order.planType;
    this.axOrderNumber = order.axOrderNumber;
    this.axInvoiceNumber = order.axInvoiceNumber;
  }

  toOrder(): IOrder {
    return {
      orderId: this.orderId,
      orderCode: this.orderCode,
      orderDate: this.orderDate,
      paymentDate: this.paymentDate,
      planId: this.planId,
      planName: this.planName,
      price: this.price,
      currencyCode: this.currencyCode,
      orderStatus: this.status.key,
      planSigningCount: this.planSigningCount,
      planType: this.planType,
      axOrderNumber: this.axOrderNumber,
      axInvoiceNumber: this.axInvoiceNumber

    };
  }
}
